import { SubContractorAPI } from '@business/api/subcontractor';
import { UserId } from '@business/entities/account';
import { CalendarEventObject } from '@business/entities/calendar_event';
import { CustomTabResponse } from '@business/entities/custom-tab';
import { TimeMaterialId } from '@business/entities/tm';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showConfirmation } from '@modules/common';
import { NewFormFormData } from '@modules/settings/SettingsPage/Components/SubcontractorTab/Tabs/ProfileFormTab/AddEditAssignFormModal/AddEditAssignFormModal';
import { ServiceRegionFormData } from '@modules/subContractor/ServiceRegionTab/SubContractorServiceRegionForm';
import { defineServiceRoleFormData } from '@modules/subContractor/ServiceRoleTab/ServiceRoleForm';
import { SubContractorSubmitApplicationFormData } from '@modules/subContractor/SubmitApplicationTab';
import { ReviewApplicationRequest } from '@business/entities/subcontractor';
import { switchMap, tap } from 'rxjs/operators';
import { FlashMessage } from '@core/components/FlashMessage';
import { t } from 'ttag';

export class SubContractorBloc extends BaseBloc {
  private readonly subcontractorRepo = new Repository<CustomTabResponse | any>({
    getItemId: subcontractor => subcontractor.userId,
  });

  readonly subcontractor$ = this.subcontractorRepo.items$;
  readonly selectCategory = this.subcontractorRepo.selectItem;

  onReset() {
    this.subcontractorRepo.reset();
  }
  fetchServiceRoles = () => {
    return SubContractorAPI.fetchServiceRoles().pipe(handleMessage({ error: requestMessage('fetch_subcontractor_error') }));
  };
  fetchServiceRegion = (id: string) => {
    return SubContractorAPI.fetchServiceRegion(id).pipe(handleMessage({ error: requestMessage('fetch_subcontractor_error') }));
  };
  fetchSubcontractorDetail = (id: string) => {
    return SubContractorAPI.fetchSubcontractorDetail(id).pipe(handleMessage({ error: requestMessage('fetch_subcontractor_error') }));
  };
  updateMedia = (data: any) => {
    return SubContractorAPI.updateMedia(data).pipe(
      this.subcontractorRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_subcontractor'),
      }),
    );
  };
  submitSubcontractorDetail = (data: SubContractorSubmitApplicationFormData) => {
    return showConfirmation('Submit Application', 'Please review your application carefully before submitting. Once submitted, the application will go into "under review" status, and you will not be able to make any changes.').pipe(
      switchMap(() => SubContractorAPI.SubmitApplicationFormData(data)),
      handleMessage({
        type: requestMessage('submit_subcontractor_detail'),
      }),
    );
  };
  upsertServiceRoles = (data: defineServiceRoleFormData) => {
    // return SubContractorAPI.upsertDocuments(data).pipe(
    //   this.subcontractorRepo.ops.addOne(item => item),
    //   handleMessage({
    //     type: requestMessage('save_service_region'),
    //   }),
    // );
  };

  upsertServiceRegion = (data: ServiceRegionFormData) => {
    return SubContractorAPI.upsertServiceRegion(data).pipe(
      handleMessage({
        type: requestMessage('save_service_region'),
      }),
    );
  };
  reviewApplicationSubmit = (data: ReviewApplicationRequest) => {
    return SubContractorAPI.reviewApplicationSubmit(data).pipe(
      handleMessage({
        type: requestMessage('subcontractor_profile_review'),
      }),
      // The review is saved even when its notification email fails — say so instead of failing silently
      tap(res => {
        if (res && res.emailSent === false) {
          FlashMessage.warning(t`Email not sent`, t`The review was saved, but the email to the subcontractor could not be sent.`);
        }
      }),
    );
  };
  destroyFile = (id: string, type: string) => {
    let message = {
      type: requestMessage('delete_certificate'),
    };
    if (type == 'document') {
      message = {
        type: requestMessage('delete_document'),
      };
    }
    return SubContractorAPI.destroyFile(id).pipe(
      this.subcontractorRepo.ops.removeOne(() => id),
      handleMessage(message),
    );
  };

  fetchSubcontractors = () => {
    return SubContractorAPI.fetchSubcontractors().pipe(handleMessage({ error: requestMessage('fetch_subcontractor_error') }));
  };

  reAssignFormSubcontractor = (data: NewFormFormData) => {
    return SubContractorAPI.reAssignFormSubcontractor(data).pipe(
      handleMessage({
        type: requestMessage('subcontractor_re_assign_form'),
      }),
    );
  };
  fetchSubcontractorWorkOrders = (userId: TimeMaterialId) => {
    return SubContractorAPI.fetchSubcontractorWorkOrders(userId).pipe();
  };
  fetchSubcontractorWorkOrderDetail = (orderId: TimeMaterialId) => {
    return SubContractorAPI.fetchSubcontractorWorkOrderDetail(orderId).pipe();
  };

  searchSubcontractors = (search: string) => {
    return SubContractorAPI.searchSubcontractors({ search }).pipe(handleMessage({ error: requestMessage('fetch_subcontractor_error') }));
  };

  fetchProviderEvents = (eventObject: CalendarEventObject) => {
    return SubContractorAPI.fetchProviderEvents(eventObject).pipe(handleMessage({ error: requestMessage('fetch_subcontractor_error') }));
  };

}
